import React, { useEffect, useMemo, useRef, useState } from 'react';
import { T, useTranslate } from '@tolgee/react';
import { SypacText } from '@sypac/component-library-react';
import { Tooltip } from 'react-tooltip';
import { LayeredProgressBarProps } from './layered-progress-bar.interface';
import { cn } from '../../lib/utils';
import { numberTwoDecimal } from '../../utils/number.util';
import { ContractStatus } from '../ContractsTable/interfaces/Contract.interface';

const LayeredProgressBar: React.FC<LayeredProgressBarProps> = ({
	type,
	totalTons,
	segments,
	heightPx = 14,
	className = '',
	valueSuffix = 't',
	showSummary = false,
	contractStatus,
	allowedOverdraftTons,
}) => {
	const { t } = useTranslate();
	const [animateIn, setAnimateIn] = useState<boolean>(false);
	const [segmentsForAnim, setSegmentsForAnim] = useState(segments);

	const total = Math.max(0, Number(totalTons) || 0);

	const overdraftTons = Math.max(0, Number(allowedOverdraftTons) || 0);
	const hasOverdraft = overdraftTons > 0;
	const visualTotal = total + (hasOverdraft ? overdraftTons : 0);

	const makeTooltipTemplate = (
		tooltip: string | undefined,
		originalValue: number,
	) => {
		if (!tooltip) return tooltip;
		if (tooltip.includes('{value}')) return tooltip;

		const candidates = [
			String(originalValue),
			String(numberTwoDecimal(originalValue)),
		];

		for (const c of candidates) {
			if (tooltip.includes(c)) {
				return tooltip.replace(c, '{value}');
			}
		}

		return tooltip;
	};

	const formatTooltipValue = (tooltip: string | undefined, value: number) => {
		if (!tooltip) return tooltip;

		const formatted = String(numberTwoDecimal(value));

		if (tooltip.includes('{value}')) {
			return tooltip.replace('{value}', formatted);
		}

		const raw = String(value);
		return tooltip.replace(raw, formatted);
	};

	const normalizedSegments = useMemo(() => {
		let used = 0;

		return segmentsForAnim
			.map((seg, idx) => ({
				...seg,
				__key: String(idx),
				value: Math.max(Number(seg.value) || 0, 0),
			}))
			.map((seg) => {
				const left = used;
				const value = seg.value;
				used += value;

				return { ...seg, left, value };
			});
	}, [segmentsForAnim, total]); // eslint-disable-line

	const filledTotal = useMemo(
		() => normalizedSegments.reduce((sum, seg) => sum + seg.value, 0),
		[normalizedSegments],
	);
	const prevFilledTotalRef = useRef(filledTotal);

	const remainingValue = numberTwoDecimal(Math.max(total - filledTotal, 0));

	const orderedOverdraftPrefix = t(
		'layeredProgressBar.orderedOverdraft',
		'Ordered overdraft:',
	);

	const segmentsForRender = useMemo(() => {
		if (total <= 0) return [];

		const out: any[] = [];

		if (hasOverdraft) {
			out.push({
				__key: 'overdraft-bg',
				isOverdraftBackground: true,
				value: overdraftTons,
				left: total,
				colorClass: 'bg-[#D6D6D6]',
				tooltip: `${t(
					'layeredProgressBar.allowedOverdraft',
					'Allowed overdraft:',
				)} {value}${valueSuffix}`,
				enableBrightnessHover: true,
			});
		}

		for (const seg of normalizedSegments) {
			const tooltipTemplate = makeTooltipTemplate(
				seg.tooltip,
				Number(seg.value) || 0,
			);

			out.push({ ...seg, tooltip: tooltipTemplate });
		}

		if (remainingValue > 0 && filledTotal < total) {
			out.push({
				__key: 'remaining',
				isRemaining: true,
				value: remainingValue,
				left: filledTotal,
				colorClass: 'bg-[#E8E8E8]',
				tooltip: `${t(
					'layeredProgressBar.remaining',
					'Remaining:',
				)} {value}${valueSuffix}`,
				enableBrightnessHover: false,
			});
		}

		return out;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		total,
		hasOverdraft,
		overdraftTons,
		visualTotal,
		normalizedSegments,
		remainingValue,
		filledTotal,
		t,
		valueSuffix,
	]);

	useEffect(() => {
		const prev = prevFilledTotalRef.current;

		if (filledTotal <= 0) {
			setAnimateIn(false);
		} else if (prev <= 0 || !animateIn) {
			setAnimateIn(false);
			requestAnimationFrame(() => setAnimateIn(true));
		}

		prevFilledTotalRef.current = filledTotal;
	}, [filledTotal, animateIn]);

	useEffect(() => {
		if (segments.length === 0 && segmentsForAnim.length > 0) {
			setSegmentsForAnim((prev) => prev.map((s) => ({ ...s, value: 0 })));

			const timeoutId = window.setTimeout(() => {
				setSegmentsForAnim([]);
			}, 170);

			return () => window.clearTimeout(timeoutId);
		}

		setSegmentsForAnim(segments);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [segments]);

	const summarySuffix = hasOverdraft
		? ` + ${numberTwoDecimal(overdraftTons)}${valueSuffix}`
		: '';

	const summaryText = `${numberTwoDecimal(
		filledTotal,
	)} / ${total}${valueSuffix}${summarySuffix}`;

	const summaryMaxText = `${numberTwoDecimal(
		visualTotal,
	)} / ${total}${valueSuffix}${summarySuffix}`;
	const summaryMinWidthCh = Math.max(summaryMaxText.length - 4, 0);

	return (
		<div className={cn('w-full flex flex-col gap-[10px]', className)}>
			{type.key || type.label ? (
				<SypacText variant="body-regular-small">
					<p className="text-xs text-gray-60 truncate">
						{type.key ? (
							<T keyName={`adminContracts.${type.key}`}>{type.label}</T>
						) : (
							type.label
						)}
					</p>
				</SypacText>
			) : null}

			<div
				className={cn(
					'flex gap-[6px] items-center border border-solid border-gray-10 rounded-md px-[3px] min-w-0',
					!showSummary && 'py-[2.5px]',
				)}
			>
				<div className="flex-1 min-w-0">
					<div
						className="relative w-full rounded overflow-hidden"
						style={{ height: heightPx, backgroundColor: '#E8E8E8' }}
					>
						{segmentsForRender.map((seg: any) => {
							const denom = Math.max(visualTotal, 1);
							const widthPct = Math.min((seg.value / denom) * 100, 100);
							const leftPct = Math.min((seg.left / denom) * 100, 100);

							const isOverdraftBg = Boolean(seg.isOverdraftBackground);

							const colorClass =
								!isOverdraftBg &&
								Math.round(filledTotal) >= total &&
								contractStatus === ContractStatus.completed
									? 'bg-cornflower-blue'
									: seg.colorClass || 'bg-mountain-meadow';

							const zIndex = isOverdraftBg ? 0 : seg.isRemaining ? 5 : 10;
							const shouldAnimate = !seg.isRemaining && !isOverdraftBg;

							const start = Number(seg.left) || 0;
							const end = start + (Number(seg.value) || 0);

							const overlapStart = Math.max(start, total);
							const overlapEnd = Math.min(end, visualTotal);
							const overlap = Math.max(overlapEnd - overlapStart, 0);

							const segValue = Math.max(Number(seg.value) || 0, 0);
							const overlayLeftPct =
								segValue > 0 ? ((overlapStart - start) / segValue) * 100 : 0;
							const overlayWidthPct =
								segValue > 0 ? (overlap / segValue) * 100 : 0;

							const tooltipContent = formatTooltipValue(seg.tooltip, seg.value);

							const overdraftTooltipTemplate = `${orderedOverdraftPrefix} {value}${valueSuffix}`;

							const overdraftTooltipContent = formatTooltipValue(
								overdraftTooltipTemplate,
								overlap,
							);

							return (
								<div
									key={seg.__key}
									className={cn(
										'absolute top-0 h-full overflow-hidden transition-all duration-150',
									)}
									style={{
										width: !animateIn && shouldAnimate ? '0%' : `${widthPct}%`,
										left: `${leftPct}%`,
										zIndex,
									}}
								>
									<div
										className={cn(
											colorClass,
											'h-full w-full',
											seg.enableBrightnessHover && 'hover:brightness-110',
										)}
										data-tooltip-id="status-badge"
										data-tooltip-content={tooltipContent}
									/>

									{hasOverdraft && !isOverdraftBg && !seg.isRemaining && (
										<div
											className={cn(
												colorClass,
												'absolute top-0 h-full transition-all duration-150 brightness-90 hover:brightness-110',
											)}
											style={{
												left: `${Math.min(Math.max(overlayLeftPct, 0), 100)}%`,
												width: `${Math.min(
													Math.max(overlayWidthPct, 0),
													100,
												)}%`,
											}}
											data-tooltip-id="status-badge"
											data-tooltip-content={overdraftTooltipContent}
										/>
									)}
								</div>
							);
						})}
					</div>
				</div>

				{showSummary && (
					<div className="shrink-0">
						<SypacText variant="body-regular-medium">
							<p
								className="text-gray-60 leading-none mt-[3px] whitespace-nowrap tabular-nums text-right"
								style={{ minWidth: `${summaryMinWidthCh}ch` }}
							>
								{summaryText}
							</p>
						</SypacText>
					</div>
				)}
			</div>

			<Tooltip
				place="top"
				id="status-badge"
				style={{
					backgroundColor: '#E8E8E8',
					color: '#000000',
				}}
			/>
		</div>
	);
};

export default LayeredProgressBar;
